import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { kbTagsApi } from '../utils/api';
import type { KBTag, KBTagTreeNode } from '../types/knowledgeBase';
import Navigation from '../components/Navigation';

// Predefined color palette
const COLOR_PALETTE = [
  '#EF4444', // Red
  '#F97316', // Orange
  '#F59E0B', // Amber
  '#EAB308', // Yellow
  '#84CC16', // Lime
  '#22C55E', // Green
  '#10B981', // Emerald
  '#14B8A6', // Teal
  '#06B6D4', // Cyan
  '#0EA5E9', // Sky
  '#3B82F6', // Blue
  '#6366F1', // Indigo
  '#8B5CF6', // Violet
  '#A855F7', // Purple
  '#D946EF', // Fuchsia
  '#EC4899', // Pink
  '#6B7280', // Gray
];

interface TagFormData {
  name: string;
  parent_id: number | null;
  color: string | null;
}

interface TagNodeProps {
  node: KBTagTreeNode;
  level: number;
  flatTags: KBTag[];
  onEdit: (tag: KBTagTreeNode) => void;
  onDelete: (tagId: number) => void;
  onCreateChild: (parentId: number) => void;
}

function TagNode({ node, level, flatTags, onEdit, onDelete, onCreateChild }: TagNodeProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const hasChildren = node.children && node.children.length > 0;

  return (
    <div>
      <div
        className="flex items-center py-2 px-3 hover:bg-gray-50 rounded-md group"
        style={{ paddingLeft: `${level * 24 + 12}px` }}
      >
        {hasChildren ? (
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="mr-2 w-5 h-5 flex items-center justify-center text-gray-400 hover:text-gray-600"
          >
            {isExpanded ? '▼' : '▶'}
          </button>
        ) : (
          <span className="mr-2 w-5" />
        )}

        {node.color && (
          <span
            className="w-4 h-4 rounded-full mr-2 flex-shrink-0"
            style={{ backgroundColor: node.color }}
          />
        )}

        <span className="flex-1 font-medium">{node.name}</span>

        <span className="text-sm text-gray-500 mr-4">
          {node.kb_count} KB{node.kb_count !== 1 ? 's' : ''}
        </span>

        <div className="opacity-0 group-hover:opacity-100 flex items-center space-x-1">
          <button
            onClick={() => onCreateChild(node.id)}
            className="px-2 py-1 text-xs text-gray-600 hover:text-gray-900 border border-gray-200 rounded hover:bg-white"
            title="Add child tag"
          >
            + Child
          </button>
          <button
            onClick={() => onEdit(node)}
            className="px-2 py-1 text-xs text-blue-600 hover:text-blue-800 border border-gray-200 rounded hover:bg-white"
          >
            Edit
          </button>
          <button
            onClick={() => onDelete(node.id)}
            className="px-2 py-1 text-xs text-red-600 hover:text-red-800 border border-gray-200 rounded hover:bg-white"
          >
            Delete
          </button>
        </div>
      </div>

      {hasChildren && isExpanded && (
        <div>
          {node.children.map((child) => (
            <TagNode
              key={child.id}
              node={child}
              level={level + 1}
              flatTags={flatTags}
              onEdit={onEdit}
              onDelete={onDelete}
              onCreateChild={onCreateChild}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default function KBTagsPage() {
  const [tags, setTags] = useState<KBTagTreeNode[]>([]);
  const [flatTags, setFlatTags] = useState<KBTag[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  // Form state
  const [showForm, setShowForm] = useState(false);
  const [editingTag, setEditingTag] = useState<KBTagTreeNode | null>(null);
  const [formData, setFormData] = useState<TagFormData>({
    name: '',
    parent_id: null,
    color: null,
  });
  const [isSaving, setIsSaving] = useState(false);

  const loadTags = async () => {
    try {
      setIsLoading(true);
      const [treeData, flatData] = await Promise.all([
        kbTagsApi.getTree(),
        kbTagsApi.getAll(),
      ]);
      setTags(treeData);
      setFlatTags(flatData);
      setError('');
    } catch (err) {
      setError('Failed to load tags');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadTags();
  }, []);

  const handleCreateNew = () => {
    setEditingTag(null);
    setFormData({ name: '', parent_id: null, color: null });
    setShowForm(true);
  };

  const handleCreateChild = (parentId: number) => {
    setEditingTag(null);
    setFormData({ name: '', parent_id: parentId, color: null });
    setShowForm(true);
  };

  const handleEdit = (tag: KBTagTreeNode) => {
    setEditingTag(tag);
    setFormData({
      name: tag.name,
      parent_id: tag.parent_id,
      color: tag.color,
    });
    setShowForm(true);
  };

  const handleDelete = async (tagId: number) => {
    const tag = flatTags.find(t => t.id === tagId);
    if (!tag) return;

    const childCount = tags.reduce((count, node) => {
      const countChildren = (n: KBTagTreeNode): number => {
        if (n.id === tagId) {
          return n.children.length;
        }
        return n.children.reduce((c, child) => c + countChildren(child), 0);
      };
      return count + countChildren(node);
    }, 0);

    const message = childCount > 0
      ? `This will delete "${tag.name}" and all its ${childCount} child tag(s). This cannot be undone.`
      : `Are you sure you want to delete "${tag.name}"? This cannot be undone.`;

    if (!confirm(message)) return;

    try {
      await kbTagsApi.delete(tagId);
      await loadTags();
    } catch (err) {
      console.error('Failed to delete tag:', err);
      alert('Failed to delete tag');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) return;

    try {
      setIsSaving(true);
      if (editingTag) {
        await kbTagsApi.update(editingTag.id, {
          name: formData.name.trim(),
          parent_id: formData.parent_id,
          color: formData.color,
        });
      } else {
        await kbTagsApi.create({
          name: formData.name.trim(),
          parent_id: formData.parent_id,
          color: formData.color,
        });
      }
      setShowForm(false);
      await loadTags();
    } catch (err) {
      console.error('Failed to save tag:', err);
      alert('Failed to save tag');
    } finally {
      setIsSaving(false);
    }
  };

  // Build flat list for parent selector (excluding current tag and its children)
  const getAvailableParents = (): Array<{ id: number; name: string; depth: number }> => {
    const result: Array<{ id: number; name: string; depth: number }> = [];

    const traverse = (nodes: KBTagTreeNode[], depth: number) => {
      for (const node of nodes) {
        // Skip if this is the tag being edited (can't be its own parent)
        if (editingTag && node.id === editingTag.id) continue;

        result.push({ id: node.id, name: node.name, depth });
        traverse(node.children, depth + 1);
      }
    };

    traverse(tags, 0);
    return result;
  };

  if (isLoading && tags.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading tags...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Manage Tags - MAIA Agent 128</title>
      </Helmet>
      <div className="min-h-screen bg-gray-50">
        <Navigation />

        <div className="max-w-4xl mx-auto px-4 pt-24 pb-8">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h1 className="text-3xl font-bold">Manage Tags</h1>
              <p className="text-gray-600 mt-1">
                Organize your knowledge bases with hierarchical tags
              </p>
            </div>
            <div className="flex items-center space-x-3">
              <Link
                to="/knowledge-bases"
                className="px-4 py-2 btn-secondary rounded-md text-sm font-medium"
              >
                Back to KBs
              </Link>
              <button
                onClick={handleCreateNew}
                className="px-4 py-2 btn-primary rounded-md text-sm font-medium"
              >
                New Tag
              </button>
            </div>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {/* Tag Form Modal */}
          {showForm && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
                <h2 className="text-xl font-medium mb-4">
                  {editingTag ? 'Edit Tag' : 'Create Tag'}
                </h2>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Tag Name
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-200 rounded-md"
                      placeholder="e.g., Clients, Projects, Topics"
                      required
                      autoFocus
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Parent Tag (optional)
                    </label>
                    <select
                      value={formData.parent_id ?? ''}
                      onChange={(e) => setFormData({
                        ...formData,
                        parent_id: e.target.value ? parseInt(e.target.value) : null,
                      })}
                      className="w-full px-4 py-2 border border-gray-200 rounded-md"
                    >
                      <option value="">None (Root tag)</option>
                      {getAvailableParents().map((parent) => (
                        <option key={parent.id} value={parent.id}>
                          {'  '.repeat(parent.depth)}{parent.depth > 0 ? '└ ' : ''}{parent.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Color (optional)
                    </label>
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, color: null })}
                        className={`w-8 h-8 rounded-full border-2 flex items-center justify-center ${
                          formData.color === null ? 'border-black' : 'border-gray-200'
                        }`}
                        title="No color"
                      >
                        <span className="text-gray-400">∅</span>
                      </button>
                      {COLOR_PALETTE.map((color) => (
                        <button
                          key={color}
                          type="button"
                          onClick={() => setFormData({ ...formData, color })}
                          className={`w-8 h-8 rounded-full border-2 ${
                            formData.color === color ? 'border-black scale-110' : 'border-transparent'
                          }`}
                          style={{ backgroundColor: color }}
                          title={color}
                        />
                      ))}
                    </div>
                  </div>

                  <div className="flex justify-end space-x-3 pt-4">
                    <button
                      type="button"
                      onClick={() => setShowForm(false)}
                      className="px-4 py-2 btn-secondary rounded-md text-sm"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={isSaving || !formData.name.trim()}
                      className="px-4 py-2 btn-primary rounded-md text-sm disabled:opacity-50"
                    >
                      {isSaving ? 'Saving...' : editingTag ? 'Save Changes' : 'Create Tag'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Tags List */}
          <div className="bg-white border border-gray-200 rounded-lg">
            {tags.length === 0 ? (
              <div className="p-12 text-center">
                <p className="text-gray-500 mb-4">No tags created yet</p>
                <button
                  onClick={handleCreateNew}
                  className="px-4 py-2 btn-primary rounded-md text-sm font-medium"
                >
                  Create Your First Tag
                </button>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {tags.map((node) => (
                  <TagNode
                    key={node.id}
                    node={node}
                    level={0}
                    flatTags={flatTags}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    onCreateChild={handleCreateChild}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Help Section */}
          <div className="mt-6 bg-gray-100 rounded-lg p-4">
            <h3 className="font-medium text-gray-900 mb-2">Tips for organizing tags</h3>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• Create parent tags for broad categories (e.g., "Clients", "Projects", "Topics")</li>
              <li>• Use child tags for specific items (e.g., "Clients → Acme Corp")</li>
              <li>• A knowledge base can have multiple tags from different hierarchies</li>
              <li>• Colors help visually distinguish tag categories</li>
            </ul>
          </div>
        </div>
      </div>
    </>
  );
}
